import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { usePlaceSearch } from "@/lib/placeSearch";

export function SuburbAutocomplete({ value, onSelect, placeholder = "Search suburb or postcode…", testid }) {
  const { search, resolve } = usePlaceSearch("suburb");
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const box = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setOpen(true);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        setResults(await search(v));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 400);
  };

  const pick = async (pr) => {
    setOpen(false);
    let r;
    try { r = await resolve(pr); } catch { return; }
    const label = `${r.suburb}${r.state ? ", " + r.state : ""}${r.postcode ? " " + r.postcode : ""}`;
    setQ(label);
    onSelect(r);
  };

  return (
    <div className="relative" ref={box}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid={testid} value={q} onChange={onChange} onFocus={() => q.length >= 2 && setOpen(true)} placeholder={placeholder} className="pl-9" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-kinetic" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <button key={r.place_id || `${r.lat},${r.lon},${r.postcode}`} type="button" onClick={() => pick(r)} data-testid={`suburb-option-${i}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2 border-b border-border/50 last:border-0">
              <MapPin className="h-3.5 w-3.5 text-kinetic shrink-0" />
              <span className="truncate">{r.place_id ? r.display.replace(/, Australia$/, "") : `${r.suburb}${r.state ? `, ${r.state}` : ""}${r.postcode ? ` ${r.postcode}` : ""}`}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
